import { Delete } from '@mui/icons-material';
import { Paper } from '@mui/material';
import { GridActionsCellItem, GridValidRowModel } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import {
  DbDeliverableType,
  useLazyGetApiProjectsDeliverableTypesQuery,
  usePutApiProjectsByIdDeliverableTypesMutation,
} from '../../../../../../app/athenaApiGen';
import DeleteConfirmation from '../../../../../../components/Modals/DeleteConfirmation';
import { StyledDataGrid } from '../../../../../../shared-theme/StyledComponents';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../../../../../../utils/tableVariables';
import { useProjectTypeContext } from '../useProjectTypesContext';
import NoProjectDeliverableData from './NoProjectDeliverableData';

const ProjectTypesTable = () => {
  const [queryProjectTypes, { data, isLoading }] =
    useLazyGetApiProjectsDeliverableTypesQuery();
  const [updateProjectTypes, { isLoading: deleting }] =
    usePutApiProjectsByIdDeliverableTypesMutation();
  const { payload, setProjectDelTypeIds, projectDelTypeIds } =
    useProjectTypeContext();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<GridValidRowModel>({});

  useEffect(() => {
    (async () => {
      if (payload.id) {
        const data = await queryProjectTypes({
          projectId: payload.id,
        }).unwrap();
        data &&
          setProjectDelTypeIds(
            data.map((delTypes) => delTypes.deliverableTypeId ?? ''),
          );
      }
    })();
  }, [queryProjectTypes, payload.id]);

  const handleDelete = useCallback(async () => {
    const data = await updateProjectTypes({
      id: payload.id,
      handlersProjectDeliverableTypesRequest: {
        deliverableTypeIds: projectDelTypeIds.filter(
          (delTypeId) => delTypeId !== selectedRow.deliverableTypeId,
        ),
      },
    }).unwrap();

    data &&
      setProjectDelTypeIds(data.map((delTypes) => delTypes.deliverableTypeId!));
    setDeleteDialogOpen(false);
  }, [payload.id, selectedRow, projectDelTypeIds]);

  return (
    <React.Fragment>
      <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <StyledDataGrid
          pagination
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          initialState={{
            pagination: {
              paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE },
            },
          }}
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          rows={payload.id ? data : []}
          getRowId={(del) => del.deliverableTypeId ?? ''}
          columns={[
            {
              field: 'deliverableNumber',
              headerName: 'Del #',
              flex: 0.5,
              valueGetter: (param: string) => param ?? '-',
            },
            {
              field: 'deliverableType',
              headerName: 'Type',
              flex: 2,
              valueGetter: (row: DbDeliverableType) => row?.title ?? '-',
            },
            {
              field: 'actions',
              type: 'actions',
              headerName: 'Actions',
              width: 100,
              cellClassName: 'actions',
              getActions: ({ row }) => {
                return [
                  <GridActionsCellItem
                    icon={<Delete sx={{ color: 'red' }} />}
                    label="Actions"
                    id="project-action-button"
                    onClick={() => {
                      setSelectedRow(row);
                      setDeleteDialogOpen(true);
                    }}
                    color="inherit"
                  />,
                ];
              },
            },
          ]}
          slots={{
            noRowsOverlay: NoProjectDeliverableData,
          }}
          loading={isLoading}
        />
      </Paper>

      <DeleteConfirmation
        open={deleteDialogOpen}
        title={`Remove ${selectedRow.deliverableType?.title}`}
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `Are you sure you want to remove <strong>${selectedRow.deliverableType?.title}</strong>? <br /><br />`,
        }}
        handleConfirmation={handleDelete}
        isLoading={deleting}
      />
    </React.Fragment>
  );
};

export default ProjectTypesTable;
